/**
 * SiFa-Betreuung — gemeinsame Typen fuer Fristen (Faelligkeiten & Vorsorge)
 * und Taetigkeitsnachweis (Betreuungszeiten). Spiegel der Tabellen aus
 * supabase/sifa_betreuung.sql (MIGRATIONS.md Nr. 92) in camelCase; der
 * Mapper snake→camel steht in sifaBetreuungService.ts.
 *
 * Bauplan: docs/sifa-betreuung-plan.md.
 */

/**
 * Kategorie einer Frist. Die Ampel ist fuer alle gleich (fristenLogic.ts);
 * die Kategorie steuert nur Symbol, Filter und den Vorschlag fuer das
 * Wiederholungsintervall beim Anlegen.
 */
export type FristKategorie =
  | 'pruefung'        // Pruefpflichtige Anlagen/Arbeitsmittel (BetrSichV, DGUV V3 ...)
  | 'vorsorge'        // Arbeitsmedizinische Vorsorge (ArbMedVV)
  | 'unterweisung'    // Wiederholungsunterweisung (jaehrlich, § 12 ArbSchG)
  | 'asa'             // Arbeitsschutzausschuss (vierteljaehrlich, § 11 ASiG)
  | 'ersthelfer'      // Ersthelfer-Fortbildung (alle 2 Jahre)
  | 'beauftragte'     // Bestellungen/Fortbildungen von Beauftragten (SiBe, Brandschutzhelfer ...)
  | 'begehung'        // Turnusbegehung des Betriebs
  | 'gbu'             // Ueberpruefung einer Gefaehrdungsbeurteilung (docs/sifa-gbu-plan.md §8)
  | 'sonstiges';

export const FRIST_KATEGORIEN: ReadonlyArray<{ id: FristKategorie; label: string; intervallMonate: number | null }> = [
  { id: 'pruefung', label: 'Prüfung (Anlagen/Arbeitsmittel)', intervallMonate: 12 },
  { id: 'vorsorge', label: 'Arbeitsmedizinische Vorsorge', intervallMonate: 36 },
  { id: 'unterweisung', label: 'Wiederholungsunterweisung', intervallMonate: 12 },
  { id: 'asa', label: 'ASA-Sitzung', intervallMonate: 3 },
  { id: 'ersthelfer', label: 'Ersthelfer-Fortbildung', intervallMonate: 24 },
  { id: 'beauftragte', label: 'Beauftragte (Bestellung/Fortbildung)', intervallMonate: null },
  { id: 'begehung', label: 'Betriebsbegehung', intervallMonate: 12 },
  // Entsteht bei jeder GBU-Freigabe (sifa_gbu_freigabe_vollziehen, faellig = pruefen_bis);
  // von Hand anlegbar, z. B. fuer eine GBU, die der Betrieb selbst fuehrt.
  { id: 'gbu', label: 'Überprüfung Gefährdungsbeurteilung', intervallMonate: 12 },
  { id: 'sonstiges', label: 'Sonstiges', intervallMonate: null },
];

/** Eine Frist je Zeile in `sifa_fristen`. */
export interface SifaFrist {
  id: string;
  projectId: string;
  /** Optional an eine Person aus dem Mitarbeiterstamm gebunden (Vorsorge, Ersthelfer). */
  mitarbeiterId: string | null;
  kategorie: FristKategorie;
  titel: string;
  /** ISO-Datum (yyyy-mm-dd). */
  faelligAm: string;
  /** Wiederholung in Monaten; null = einmalig. Beim Erledigen entsteht die naechste Zeile. */
  intervallMonate: number | null;
  /** ISO-Datum; gesetzt = erledigt (Zeile bleibt als Historie stehen). */
  erledigtAm: string | null;
  /** Freitext: wer im Betrieb zustaendig ist. */
  zustaendig: string | null;
  notiz: string | null;
  /** Verweis auf die Vorgaenger-Zeile, aus der diese durch Erledigen entstand. */
  vorgaengerId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Art einer SiFa-Taetigkeit (Taetigkeitsnachweis nach DGUV Vorschrift 2, Anlage 3). */
export type TaetigkeitArt =
  | 'begehung'
  | 'beratung'
  | 'asa'
  | 'unterweisung'
  | 'gbu'             // Gefaehrdungsbeurteilung (Erstellung/Fortschreibung)
  | 'unfall'          // Unfalluntersuchung
  | 'schulung'
  | 'dokumentation'
  | 'fahrt'
  | 'sonstiges';

export const TAETIGKEIT_ARTEN: ReadonlyArray<{ id: TaetigkeitArt; label: string }> = [
  { id: 'begehung', label: 'Begehung' },
  { id: 'beratung', label: 'Beratung' },
  { id: 'asa', label: 'ASA-Sitzung' },
  { id: 'unterweisung', label: 'Unterweisung' },
  { id: 'gbu', label: 'Gefährdungsbeurteilung' },
  { id: 'unfall', label: 'Unfalluntersuchung' },
  { id: 'schulung', label: 'Schulung' },
  { id: 'dokumentation', label: 'Dokumentation' },
  { id: 'fahrt', label: 'Fahrzeit' },
  { id: 'sonstiges', label: 'Sonstiges' },
];

/** Eine Taetigkeit je Zeile in `sifa_taetigkeiten`. */
export interface SifaTaetigkeit {
  id: string;
  projectId: string;
  /** ISO-Datum (yyyy-mm-dd). */
  datum: string;
  dauerMinuten: number;
  art: TaetigkeitArt;
  titel: string;
  beschreibung: string | null;
  /** Anzeigename dessen, der die Taetigkeit ausgefuehrt hat (aus dem Profil zum Zeitpunkt der Erfassung). */
  durchgefuehrtVon: string | null;
  /** Nutzer-Id des Erfassers. */
  createdBy: string | null;
  /** Bezug auf einen Begehungsbericht (Nummer) oder eine Unterweisung — nur Anzeige. */
  bezug: string | null;
  /** Abrechnungsriegel wie bei den Teilnahmen: null = nicht abgerechnet (Stufe 3). */
  gebuchtAm: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Kennzahlen fuer Kachel und Betreuungskarte im Hub. Alle Zahlen aus
 * head-Counts bzw. einer Summenabfrage — nie aus vollstaendig geladenen
 * Listen (useSifaHubData).
 */
export interface SifaKennzahlen {
  /** Fristen mit faellig_am < heute und erledigt_am null. */
  fristenUeberfaellig: number;
  /** Fristen faellig in den naechsten 60 Tagen (heute eingeschlossen), nicht erledigt. */
  fristenBald: number;
  /** Naechste offene Frist (Titel + Datum), null wenn keine. */
  naechsteFrist: { titel: string; faelligAm: string } | null;
  /** Summe dauer_minuten aller Taetigkeiten im laufenden Kalenderjahr. */
  minutenJahr: number;
  /** Anzahl Taetigkeiten im laufenden Kalenderjahr. */
  taetigkeitenJahr: number;
}

export const KENNZAHLEN_LEER: SifaKennzahlen = {
  fristenUeberfaellig: 0,
  fristenBald: 0,
  naechsteFrist: null,
  minutenJahr: 0,
  taetigkeitenJahr: 0,
};
